import { useState, useContext } from "react";
import { supabase } from "../supabase";
import { composeName, NAME_SUFFIXES } from "../utils/names";
import { AuthContext, AdminContext } from "../contexts";
import { Btn } from "../components/ui";
import { ensureProfile } from "../utils/helpers";
import EmailVerificationView from "./EmailVerificationView";

const AuthView = ({ setView, initialAuthMode, authLinkError, setAuthLinkError }) => {
  const { setUser } = useContext(AuthContext);
  const { autoApprove } = useContext(AdminContext);
  const [mode, setMode] = useState(initialAuthMode || "signin");
  const [form, setForm] = useState({ email: "", password: "", firstName: "", middleName: "", lastName: "", suffix: "" });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async () => {
    setError("");
    if (!form.email) { setError("Enter your email address first."); return; }
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(form.email, {
      redirectTo: window.location.origin,
    });
    if (resetError) { setError(resetError.message); setLoading(false); return; }
    setResetSent(true);
    setLoading(false);
  };

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    if (!form.email || !form.password) { setError("All fields required."); setLoading(false); return; }
    if (mode === "signup" && (!form.firstName.trim() || !form.lastName.trim())) { setError("First and last name are required."); setLoading(false); return; }
    if (mode === "signup" && !ageConfirmed) { setError("You must confirm you are 18 or older to create an account."); setLoading(false); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); setLoading(false); return; }
    if (mode === "signup" && !/[A-Z]/.test(form.password)) { setError("Password must include an uppercase letter."); setLoading(false); return; }
    if (mode === "signup" && !/[a-z]/.test(form.password)) { setError("Password must include a lowercase letter."); setLoading(false); return; }
    if (mode === "signup" && !/[0-9]/.test(form.password)) { setError("Password must include a number."); setLoading(false); return; }

    if (mode === "signin") {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });
      if (authError) {
        // Catch unverified email attempts
        if (authError.message.toLowerCase().includes("email not confirmed")) {
          setError("Please verify your email before signing in. Check your inbox for the confirmation link.");
        } else {
          setError(authError.message);
        }
        setLoading(false);
        return;
      }

      const profile = await ensureProfile(data.user);

      const userObj = {
        id: data.user.id,
        email: data.user.email,
        name: profile?.name || data.user.email.split("@")[0],
        isAdmin: profile?.is_admin || false,
        role: profile?.role || "customer",
        status: profile?.status || "active",
        marketingOptIn: profile?.marketing_opt_in || false,
      };
      setUser(userObj);
      if (userObj.status === "pending") { setView("pending"); }
      else if (userObj.isAdmin || userObj.role === "manager") { setView("admin"); }
      else { setView("shop"); }

    } else {
      // Sign up — sends verification email, does NOT log in
      const fullName = composeName(form);
      const status = autoApprove ? "active" : "pending";
      const { data, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            name: fullName,
            first_name: form.firstName.trim(),
            middle_name: form.middleName.trim(),
            last_name: form.lastName.trim(),
            suffix: form.suffix.trim(),
            marketing_opt_in: marketingOptIn,
            approval_status: status,
          },
        },
      });
      if (authError) { setError(authError.message); setLoading(false); return; }

      // If the user already exists but hasn't confirmed, identities will be empty
      if (data?.user?.identities?.length === 0) {
        setError("An account with this email already exists. Please sign in or check your email for a verification link.");
        setLoading(false);
        return;
      }

      // Create profile row
      if (data?.user?.id) {
        await supabase.from("profiles").insert({
          id: data.user.id,
          name: fullName,
          email: form.email,
          is_admin: false,
          marketing_opt_in: marketingOptIn,
          status,
        });

        // If manual approval, add to pending_users queue
        if (!autoApprove) {
          await supabase.from("pending_users").insert({
            auth_user_id: data.user.id,
            name: fullName,
            email: form.email,
            marketing_opt_in: marketingOptIn,
            status: "pending",
          });
        }
      }

      // Show "check your email" screen instead of logging in
      setVerificationSent(true);
    }
    setLoading(false);
  };

  if (verificationSent) {
    return <EmailVerificationView email={form.email} setView={setView} />;
  }

  if (mode === "forgot") {
    return (
      <div style={{ maxWidth: 420, margin: "64px auto", padding: "0 24px" }} className="fade-up">
        <h1 style={{ fontSize: "36px", marginBottom: 6 }}>Reset password</h1>
        <p style={{ color: "var(--text-dim)", marginBottom: 32 }}>
          Enter your email and we'll send you a link to reset your password.
        </p>
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 28 }}>
          {resetSent ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "40px", marginBottom: 12 }}>📧</div>
              <h3 style={{ fontSize: "20px", marginBottom: 8 }}>Check your email</h3>
              <p style={{ color: "var(--text-dim)", fontSize: "14px", lineHeight: 1.6, marginBottom: 8 }}>
                If an account exists for <span style={{ fontFamily: "var(--font-mono)", color: "var(--amber)" }}>{form.email}</span>, you'll receive a password reset link shortly.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email address" />
              {error && <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)" }}>{error}</p>}
              <Btn onClick={handleForgotPassword} disabled={loading} style={{ justifyContent: "center" }}>
                {loading ? "Please wait…" : "Send Reset Link"}
              </Btn>
            </div>
          )}
          <button onClick={() => { setMode("signin"); setError(""); setResetSent(false); }} style={{ marginTop: 16, fontSize: "13px", color: "var(--text-dim)", fontFamily: "var(--font-mono)", display: "block", width: "100%", textAlign: "center" }}>
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: "64px auto", padding: "0 24px" }} className="fade-up">
      <h1 style={{ fontSize: "36px", marginBottom: 6 }}>{mode === "signin" ? "Welcome back" : "Create account"}</h1>
      <p style={{ color: "var(--text-dim)", marginBottom: 32 }}>
        {mode === "signin" ? "Sign in to access the shop." : "Join to start shopping."}
      </p>

      {authLinkError && (
        <div style={{ background: "rgba(217,64,64,0.1)", border: "1px solid var(--red)", borderRadius: "var(--radius)", padding: "14px 18px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)", margin: 0 }}>{authLinkError}</p>
          <button onClick={() => setAuthLinkError(null)} style={{ color: "var(--red)", background: "none", border: "none", cursor: "pointer", fontSize: "16px", padding: "0 4px" }}>✕</button>
        </div>
      )}

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 28 }}>
        <div style={{ display: "flex", gap: 0, marginBottom: 24, border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
          {["signin", "signup"].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(""); setAuthLinkError?.(null); }} style={{ flex: 1, padding: "10px", fontSize: "13px", fontWeight: 700, background: mode === m ? "var(--amber)" : "transparent", color: mode === m ? "#111" : "var(--text-dim)", transition: "all 0.15s" }}>
              {m === "signin" ? "Sign In" : "Sign Up"}
            </button>
          ))}
        </div>

        <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} style={{ display: "flex", flexDirection: "column", gap: 12 }} autoComplete="on">
          {mode === "signup" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <input autoComplete="given-name" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="First name *" />
                <input autoComplete="family-name" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Last name *" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 12 }}>
                <input autoComplete="additional-name" value={form.middleName} onChange={e => setForm({ ...form, middleName: e.target.value })} placeholder="Middle name (optional)" />
                <select autoComplete="honorific-suffix" value={form.suffix} onChange={e => setForm({ ...form, suffix: e.target.value })}>
                  {NAME_SUFFIXES.map(sfx => <option key={sfx} value={sfx}>{sfx || "Suffix (opt)"}</option>)}
                </select>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginTop: 4 }}>
                <input type="checkbox" checked={ageConfirmed} onChange={e => setAgeConfirmed(e.target.checked)} style={{ width: "auto", accentColor: "var(--amber)", flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.5 }}>
                  I confirm that I am 18 years of age or older. All products are for laboratory and research use only, not for human consumption.
                </span>
              </label>
            </>
          )}
          <input type="email" autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email address" />
          <input type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Password (min. 6 characters)" />

          {mode === "signin" && (
            <button type="button" onClick={() => { setMode("forgot"); setError(""); }} style={{ fontSize: "13px", color: "var(--amber)", fontFamily: "var(--font-mono)", textAlign: "right", marginTop: -4 }}>
              Forgot password?
            </button>
          )}

          {/* Marketing opt-in — explicit, unchecked by default (CAN-SPAM / GDPR compliant) */}
          {mode === "signup" && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginTop: 4 }}>
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={e => setMarketingOptIn(e.target.checked)}
                style={{ width: "auto", marginTop: 2, accentColor: "var(--amber)", flexShrink: 0 }}
              />
              <span style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.5 }}>
                I'd like to receive occasional emails about sales and new products.
                You can unsubscribe at any time. See our{" "}
                <span style={{ color: "var(--amber)", textDecoration: "underline", cursor: "pointer" }}>privacy policy</span>.
              </span>
            </label>
          )}

          {error && <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)" }}>{error}</p>}
          <Btn type="submit" disabled={loading} style={{ justifyContent: "center", marginTop: 4 }}>
            {loading ? "Please wait…" : (mode === "signin" ? "Sign In" : "Create Account")}
          </Btn>
        </form>
      </div>

      {mode === "signup" && (
        <p style={{ color: "var(--text-muted)", fontSize: "11px", marginTop: 14, lineHeight: 1.6, textAlign: "center", fontFamily: "var(--font-mono)" }}>
          By creating an account you agree to our Terms of Service.
          {!autoApprove && " New accounts require admin approval before you can shop."}
        </p>
      )}

    </div>
  );
};

export default AuthView;
